import {
  MixpanelEvents,
  currencyFormatter,
  IEscalationOutcome,
  ICreatePaymentPromiseRequest,
  InvoiceQueryReasonLabels,
  IOpenInvoiceQueryRequest,
  langauge,
  PolicyEscalationType,
  toDecimal,
} from "shared";
import {
  ComponentType,
  ReactNode,
  SVGProps,
  useState,
} from "react";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import {
  ArrowTopRightOnSquareIcon,
  BoltIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  DocumentChartBarIcon,
  TagIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

import { Heading } from "../../../../common/Atoms/Typography/Heading";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { Pill } from "../../../../common/Atoms/Pill";
import { WrappedLink } from "../../WrappedLink";
import { ExpectedPaymentDate } from "../../Common/ExpectedPaymentDate";
import { PaymentPromiseForm } from "../../Common/PaymentPromiseForm";
import { InvoiceQueryForm } from "../../Common/InvoiceQueryForm";
import { Button } from "../../../../common/Atoms/Button";
import { Modal } from "../../../../common/Atoms/Modal";
import {
  useAddNoteMutation,
  useAssignEscalationMutation,
  useClearAssignedEscalationMutation,
} from "../../../services/api/escalationApi/escalation";
import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";
import { Assignee } from "../../Common/Assignee";
import { DatePopover } from "../../../../common/Components/DatePopover";
import { getEscalationDisplayStatus } from "../EscalationTable";
import { useCurrentEscalation } from "../../../hooks/useCurrentEscalation";
import { useAdhocActionModal } from "../../../hooks/useAdhocActionModal";
import { iconMap } from "../../../constants/iconMap";
import { mixpanelService } from "../../../../common/lib/mixpanel";
import { ContactPersons } from "../../CRM/ContactPage/ContactPersonsSection/ContactPersons";
import { TagBadge } from "../../Molecules/TagBadge";
import { useContactTags } from "../../../hooks/useContactTags";
import { useInvoiceTags } from "../../../hooks/useInvoiceTags";

const actionButtonClasses = `inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm font-medium text-slate-700 transition-colors duration-200 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 cursor-pointer focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-paidniceGreen-500`;

interface DetailRowProps {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  children: ReactNode;
}

function DetailRow({ icon: Icon, label, children }: DetailRowProps) {
  return (
    <div className={ `flex items-center justify-between gap-3 py-2.5` }>
      <dt className={ `flex shrink-0 items-center gap-2 text-sm text-slate-500` }>
        <Icon
          className={ `h-4 w-4 text-slate-400` }
          aria-hidden={ `true` }
        />
        { label }
      </dt>
      <dd className={ `flex min-w-0 items-center justify-end text-right text-sm font-medium text-slate-900` }>
        { children }
      </dd>
    </div>
  );
}

export function Overview() {
  const [addNoteApiCall] = useAddNoteMutation();
  const [assignEscalationApiCall, { isLoading: assignLoading }] = useAssignEscalationMutation();
  const [clearAssignedUserApiCall, { isLoading: clearAssignLoading }] = useClearAssignedEscalationMutation();
  const organisation = useGetSelectedOrganisation();
  const { currentEscalation: escalation } = useCurrentEscalation();
  const { openSendInvoiceEmail, openSendStatement } = useAdhocActionModal();
  const SendReminderEmailIcon = iconMap.sendReminderEmail;
  const { colorMap: contactTagColors } = useContactTags();
  const { colorMap: invoiceTagColors } = useInvoiceTags();

  const [promiseOpen, setPromiseOpen] = useState(false);
  const [queryOpen, setQueryOpen] = useState(false);

  async function onRecordPromise(promise: ICreatePaymentPromiseRequest) {
    if (!escalation) return;

    const formattedDate = DateTime.fromISO(promise.promisedDate, { zone: organisation.validatedTimezone }).toFormat(`ccc dd LLL yyyy`);

    try {
      await addNoteApiCall({
        organisationId: escalation.organisationId,
        escalationId: escalation.id,
        note: `Customer promised to pay by ${formattedDate}`,
        outcome: IEscalationOutcome.PAYMENT_PROMISE,
        promise,
      }).unwrap();

      toast.success(`Payment promise recorded`);
      setPromiseOpen(false);
    }
    catch (err) {
      toast.error(err?.data?.message || err?.data || `Could not record the payment promise`);
    }
  }

  async function onRecordQuery(query: IOpenInvoiceQueryRequest) {
    if (!escalation) return;

    try {
      await addNoteApiCall({
        organisationId: escalation.organisationId,
        escalationId: escalation.id,
        note: `Customer raised a query: ${InvoiceQueryReasonLabels[query.reason]}`,
        outcome: IEscalationOutcome.QUERY_RAISED,
        query,
      }).unwrap();

      toast.success(`Query opened`);
      setQueryOpen(false);
    }
    catch (err) {
      toast.error(err?.data?.message || err?.data || `Could not open the query`);
    }
  }

  // For esclation save
  async function onSaveExpectedDate(date: string) {
    if (!escalation) return;

    if (!date) return;

    const formattedDate = DateTime.fromISO(date, { zone: organisation.validatedTimezone }).toFormat(`ccc dd LLL yyyy`);

    await addNoteApiCall({
      organisationId: escalation.organisationId,
      escalationId: escalation.id,
      expectedPaymentDate: date ? date : null,
      note: `Expected payment date set to ${formattedDate}`,
      outcome: IEscalationOutcome.SET_EXPECTED_PAYMENT_DATE,
    });
  }

  function assign(userId: string | null) {
    if (!escalation) return;

    if (!userId) {
      unassign();

      return;
    }

    assignEscalationApiCall({
      organisationId: escalation.organisationId,
      escalationId: escalation.id,
      userId,
    });
  }

  function unassign() {
    if (!escalation) return;

    if (!escalation.assignedToUserId) return;

    clearAssignedUserApiCall({
      organisationId: escalation.organisationId,
      escalationId: escalation.id,
    });
  }

  if (!escalation || !organisation) return null;

  const invoice = escalation.invoice;
  const formatter = currencyFormatter(invoice?.xero_currency || organisation.baseCurrency);

  const invoicePill = getInvoicePill(invoice, organisation.currentTime);
  const typeLabel = getEscalationTypeLabel(escalation.escalationType);

  return (
    <section
      aria-label={ `Escalation details` }
      className={ `rounded-lg bg-white shadow-sm` }
    >
      { /* Header: title + status */ }
      <div className={ `flex items-start justify-between gap-3 px-4 py-4 sm:px-5` }>
        <div className={ `min-w-0` }>
          <Heading>
            { `Escalation Details` }
          </Heading>
          { escalation.policy?.title ? (
            <Paragraph
              variant={ `secondary` }
              className={ `mt-0.5 flex items-center gap-1.5 truncate` }
              as={ `div` }
            >
              <BoltIcon
                className={ `h-3.5 w-3.5 shrink-0 text-slate-400` }
                aria-hidden={ `true` }
              />
              { `Triggered by ` }
              <WrappedLink
                to={ `/groups/${escalation.group.id}/policies/${escalation.policyId}` }
              >
                <Paragraph
                  variant={ `link` }
                  as={ `span` }
                >
                  { escalation.policy.title }
                </Paragraph>
              </WrappedLink>
            </Paragraph>
          ) : null }
        </div>
        <div className={ `flex shrink-0 flex-col items-end gap-1.5` }>
          { getEscalationDisplayStatus(escalation) }
          <Pill
            color={ `indigo` }
            bordered
            message={ typeLabel }
          />
        </div>
      </div>

      { /* Key details */ }
      <div className={ `border-t border-slate-100 px-4 py-2 sm:px-5` }>
        <dl>
          <DetailRow
            icon={ CalendarDaysIcon }
            label={ `Escalation Date` }
          >
            <DatePopover
              date={ escalation.escalationDate }
              labelFormat={ `ccc dd LLL yyyy` }
              organisationTimezone={ organisation.validatedTimezone }
            />
          </DetailRow>

          <DetailRow
            icon={ BuildingOfficeIcon }
            label={ `Customer` }
          >
            <WrappedLink
              to={ `/contacts/${escalation.contact?.id}` }
              className={ `truncate text-indigo-600 hover:text-indigo-800 hover:underline` }
            >
              { escalation.contact?.name }
            </WrappedLink>
          </DetailRow>

          <DetailRow
            icon={ UserCircleIcon }
            label={ `Assignee` }
          >
            <div className={ `w-44 max-w-full text-left` }>
              <Assignee
                current={ escalation.assignedToUserId || null }
                onChange={ assign }
                loading={ assignLoading || clearAssignLoading }
              />
            </div>
          </DetailRow>

          { escalation.escalationSteps ? (
            <div className={ `py-2.5` }>
              <dt className={ `flex items-center gap-2 text-sm text-slate-500` }>
                <ClipboardDocumentListIcon
                  className={ `h-4 w-4 text-slate-400` }
                  aria-hidden={ `true` }
                />
                { `Instructions` }
              </dt>
              <dd className={ `mt-1.5 whitespace-pre-line rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700` }>
                { escalation.escalationSteps }
              </dd>
            </div>
          ) : null }

          { escalation.contact?.tags?.length ? (
            <DetailRow
              icon={ TagIcon }
              label={ `Contact Tags` }
            >
              <div className={ `flex flex-wrap justify-end gap-1` }>
                { escalation.contact.tags.map(tag => (
                  <TagBadge
                    key={ tag }
                    name={ tag }
                    color={ contactTagColors[tag] }
                  />
                )) }
              </div>
            </DetailRow>
          ) : null }

          { invoice?.tags?.length ? (
            <DetailRow
              icon={ TagIcon }
              label={ `Invoice Tags` }
            >
              <div className={ `flex flex-wrap justify-end gap-1` }>
                { (invoice.tags as { name: string }[]).map(({ name }) => (
                  <TagBadge
                    key={ name }
                    name={ name }
                    color={ invoiceTagColors[name] }
                  />
                )) }
              </div>
            </DetailRow>
          ) : null }

          { escalation.trackingCategories?.length ? (
            <DetailRow
              icon={ TagIcon }
              label={ `Tracking` }
            >
              <span className={ `truncate` }>{ escalation.trackingCategories.join(`, `) }</span>
            </DetailRow>
          ) : null }
        </dl>
      </div>

      { /* Invoice summary */ }
      { invoice ? (
        <div className={ `border-t border-slate-100 px-4 py-4 sm:px-5` }>
          <div className={ `rounded-lg border border-slate-200 bg-slate-50/60 p-3` }>
            <div className={ `flex items-start justify-between gap-2` }>
              <div className={ `min-w-0` }>
                { invoice.xero_number ? (
                  <WrappedLink
                    to={ `/invoices/${invoice.id}` }
                    className={ `inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline` }
                  >
                    { invoice.xero_number }
                    <ArrowTopRightOnSquareIcon
                      className={ `h-4 w-4 shrink-0` }
                      aria-hidden={ `true` }
                    />
                  </WrappedLink>
                ) : (
                  <span className={ `text-sm font-medium text-slate-700` }>
                    { `Invoice` }
                  </span>
                ) }
                { invoice.due_date_string ? (
                  <div className={ `mt-0.5 flex items-center gap-1 text-xs text-slate-500` }>
                    { `Due` }
                    <DatePopover
                      date={ invoice.due_date_string }
                      labelFormat={ `ccc dd LLL yyyy` }
                      organisationTimezone={ organisation.validatedTimezone }
                    />
                  </div>
                ) : null }
              </div>
              <div className={ `shrink-0 text-right` }>
                <div className={ `text-base font-semibold tabular-nums text-slate-900` }>
                  { formatter(toDecimal(invoice.xero_amount_due)) }
                </div>
                <div className={ `text-xs text-slate-500` }>
                  { `Total due` }
                </div>
              </div>
            </div>

            <div className={ `mt-3 flex items-center justify-between gap-2 border-t border-slate-200/70 pt-2.5` }>
              <span className={ `shrink-0 text-sm text-slate-500` }>
                { `Expected payment` }
              </span>
              <ExpectedPaymentDate
                onSave={ onSaveExpectedDate }
                currentDate={ invoice.expected_payment_date_str }
                position={ `left` }
              />
            </div>

            { invoice.xero_status === `AUTHORISED` ? (
              <div className={ `mt-2 flex items-center justify-between gap-2` }>
                <span className={ `shrink-0 text-sm text-slate-500` }>
                  { `Payment promise` }
                </span>
                <Button color={ `none` }
                  onClick={ () => setPromiseOpen(true) }>
                  { `Customer promised to pay` }
                </Button>
              </div>
            ) : null }

            { invoice.xero_status === `AUTHORISED` ? (
              <div className={ `mt-2 flex items-center justify-between gap-2` }>
                <span className={ `shrink-0 text-sm text-slate-500` }>
                  { `Query` }
                </span>
                <Button color={ `none` }
                  onClick={ () => setQueryOpen(true) }>
                  { `Customer raised a query` }
                </Button>
              </div>
            ) : null }

            { invoicePill ? (
              <div className={ `mt-2.5` }>
                { invoicePill }
              </div>
            ) : null }
          </div>
        </div>
      ) : null }

      { /* Contact people */ }
      { escalation.contact ? (
        <div className={ `border-t border-slate-100 px-4 py-4 sm:px-5` }>
          <ContactPersons embedded />
        </div>
      ) : null }

      { /* Quick actions */ }
      { (escalation.contact || invoice) ? (
        <div className={ `flex flex-wrap gap-2 border-t border-slate-100 px-4 py-3 sm:px-5` }>
          { escalation.contact ? (
            <button
              type={ `button` }
              className={ actionButtonClasses }
              onClick={ () => {
                openSendStatement(escalation.contact.id, escalation.contact.name);
                mixpanelService.trackMixpanelEvent(MixpanelEvents.ContactPageSendStatementNowClick);
              } }
            >
              <DocumentChartBarIcon className={ `h-4 w-4` } />
              { `Send Statement` }
            </button>
          ) : null }
          { invoice ? (
            <button
              type={ `button` }
              className={ actionButtonClasses }
              onClick={ () => openSendInvoiceEmail(invoice.id, invoice.xero_number || ``) }
            >
              <SendReminderEmailIcon className={ `h-4 w-4` } />
              { `Send Reminder Email` }
            </button>
          ) : null }
        </div>
      ) : null }

      <Modal
        open={ promiseOpen }
        setOpen={ setPromiseOpen }
        title={ `Customer promised to pay` }
      >
        { invoice ? (
          <PaymentPromiseForm
            amountDueCents={ invoice.xero_amount_due ?? 0 }
            currency={ invoice.xero_currency }
            onSubmit={ onRecordPromise }
            onCancel={ () => setPromiseOpen(false) }
          />
        ) : null }
      </Modal>

      <Modal
        open={ queryOpen }
        setOpen={ setQueryOpen }
        title={ `Customer raised a query` }
      >
        <InvoiceQueryForm
          onSubmit={ onRecordQuery }
          onCancel={ () => setQueryOpen(false) }
        />
      </Modal>
    </section>
  );
}

/** Clean type label derived from the shared language map (labels there carry emoji) */
function getEscalationTypeLabel(type: PolicyEscalationType) {
  const timelineLabel = langauge.escalation.type[type]?.timelineLabel;

  if (!timelineLabel) return type;

  return timelineLabel.charAt(0).toUpperCase() + timelineLabel.slice(1);
}

/** Payment state pill for the invoice summary — mirrors the kanban card language */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getInvoicePill(invoice: any, currentTime?: string | Date) {
  if (!invoice) return null;

  if (invoice.xero_amount_due === 0) {
    return (
      <Pill
        color={ `green` }
        message={ `Paid` }
        dot
      />
    );
  }

  if (invoice.xero_status !== `AUTHORISED`) return null;

  if (!invoice.due_date_string || !currentTime) return null;

  const dueDate = new Date(invoice.due_date_string);
  const now = new Date(currentTime);
  const daysOverdue = Math.floor((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));

  if (daysOverdue > 0) {
    return (
      <Pill
        color={ `red` }
        message={ `${daysOverdue}d overdue` }
        dot
      />
    );
  }

  if (daysOverdue === 0) {
    return (
      <Pill
        color={ `amber` }
        message={ `Due today` }
        dot
      />
    );
  }

  return (
    <Pill
      color={ `green` }
      message={ `${Math.abs(daysOverdue)}d until due` }
      dot
    />
  );
}
